import { cva, type VariantProps } from 'class-variance-authority'
import { cn, getInitials } from '@/lib/utils'

const avatarVariants = cva(
  // Base styles
  [
    'relative inline-flex items-center justify-center',
    'rounded-full overflow-hidden',
    'bg-primary-100 text-primary-700',
    'font-medium',
    'flex-shrink-0',
  ],
  {
    variants: {
      size: {
        xs: 'w-6 h-6 text-xs',
        sm: 'w-8 h-8 text-xs',
        md: 'w-10 h-10 text-sm',
        lg: 'w-12 h-12 text-base',
        xl: 'w-16 h-16 text-lg',
        '2xl': 'w-20 h-20 text-xl',
      },
      ring: {
        true: 'ring-2 ring-white ring-offset-2 ring-offset-surface-50',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

export interface AvatarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  src?: string | null
  alt?: string
  name?: string
  status?: 'online' | 'offline' | 'busy' | 'away'
}

export function Avatar({
  className,
  size,
  ring,
  src,
  alt,
  name,
  status,
  ...props
}: AvatarProps) {
  const initials = name ? getInitials(name) : '?'

  const statusColors = {
    online: 'bg-success-500',
    offline: 'bg-surface-400',
    busy: 'bg-danger-500',
    away: 'bg-warning-500',
  }

  const statusSizes = {
    xs: 'w-1.5 h-1.5 -right-0 -bottom-0',
    sm: 'w-2 h-2 -right-0 -bottom-0',
    md: 'w-2.5 h-2.5 -right-0.5 -bottom-0.5',
    lg: 'w-3 h-3 -right-0.5 -bottom-0.5',
    xl: 'w-3.5 h-3.5 -right-0.5 -bottom-0.5',
    '2xl': 'w-4 h-4 -right-1 -bottom-1',
  }

  return (
    <div
      className={cn(avatarVariants({ size, ring, className }))}
      {...props}
    >
      {src ? (
        <img
          src={src}
          alt={alt || name || 'Avatar'}
          className="w-full h-full object-cover"
        />
      ) : (
        <span>{initials}</span>
      )}
      {status && (
        <span
          className={cn(
            'absolute rounded-full border-2 border-white',
            statusColors[status],
            statusSizes[size || 'md']
          )}
        />
      )}
    </div>
  )
}

// Avatar group for showing multiple users
interface AvatarGroupProps {
  users: Array<{ name?: string; src?: string }>
  max?: number
  size?: VariantProps<typeof avatarVariants>['size']
  className?: string
}

export function AvatarGroup({ users, max = 4, size = 'md', className }: AvatarGroupProps) {
  const visible = users.slice(0, max)
  const remaining = users.length - max

  const overlapClasses = {
    xs: '-ml-2',
    sm: '-ml-2',
    md: '-ml-3',
    lg: '-ml-4',
    xl: '-ml-5',
    '2xl': '-ml-6',
  }

  return (
    <div className={cn('flex items-center', className)}>
      {visible.map((user, index) => (
        <Avatar
          key={index}
          name={user.name}
          src={user.src}
          size={size}
          ring
          className={index > 0 ? overlapClasses[size || 'md'] : ''}
        />
      ))}
      {remaining > 0 && (
        <div
          className={cn(
            avatarVariants({ size }),
            overlapClasses[size || 'md'],
            'bg-surface-200 text-surface-600 ring-2 ring-white'
          )}
        >
          +{remaining}
        </div>
      )}
    </div>
  )
}
